/**
 * Reads env vars, with localStorage runtime overrides for the vars in
 * OVERRIDABLE_ENV_VARS; all other vars come straight from the build-time env.
 */

import { z } from "zod";
import { DIOPTRA_LOGIN_METHOD } from "../constants";

const ENV_STORAGE_KEY = "dioptra_env_overrides";

export const OVERRIDABLE_ENV_VARS = [
  "WEBAPP_API_BASE_URL",
  "AUTH_URL", 
  "API_BASE_URL"
] as const;

export type OverridableEnvVar = typeof OVERRIDABLE_ENV_VARS[number];

let envOverrides: Partial<Record<OverridableEnvVar, string>> = {};

// __ENV__ (webpack DefinePlugin) may be a JSON string or an already-parsed object.
declare const __ENV__: string | Record<string, string | undefined>;

let envCache: Record<string, string | undefined> | null = null;

function getEnvObject(): Record<string, string | undefined> {
  if (envCache === null) {
    try {
      if (typeof __ENV__ !== 'undefined') {
        if (typeof __ENV__ === 'string') {
          envCache = JSON.parse(__ENV__);
        } else if (typeof __ENV__ === 'object' && __ENV__ !== null) {
          envCache = __ENV__ as Record<string, string | undefined>;
        } else {
          envCache = {};
        }
      } else {
        console.warn('__ENV__ is undefined - webpack DefinePlugin may not be configured correctly');
        envCache = {};
      }
    } catch (e) {
      console.warn('Failed to parse __ENV__:', e, '__ENV__ type:', typeof __ENV__, '__ENV__ value:', __ENV__);
      envCache = {};
    }
  }
  return envCache;
}

// __ENV__ supports dynamic key access; process.env only resolves keys the bundler
// statically replaces, so it's the fallback.
function getEnvValue(key: string): string | undefined {
  const envObj = getEnvObject();
  if (envObj[key] !== undefined) {
    return envObj[key];
  }
  return process.env[key];
}

const originalEnvValues: Record<OverridableEnvVar, string | undefined> = {
  WEBAPP_API_BASE_URL: getEnvValue("WEBAPP_API_BASE_URL"),
  AUTH_URL: getEnvValue("AUTH_URL"),
  API_BASE_URL: getEnvValue("API_BASE_URL"),
};

const overrideValueSchema = z.string().trim().min(1);
const storedEnvOverridesSchema = z.looseObject({});

function isOverridableEnvVar(key: string): key is OverridableEnvVar {
  return (OVERRIDABLE_ENV_VARS as readonly string[]).includes(key);
}

/**
 * Initialize env overrides from localStorage.
 * This should be called as early as possible in the app lifecycle.
 */
export function initEnvOverrides(): void {
  try {
    const stored = localStorage.getItem(ENV_STORAGE_KEY);
    if (!stored) return;

    const payload = storedEnvOverridesSchema.safeParse(JSON.parse(stored));
    if (!payload.success) {
      console.error("Ignoring malformed env overrides in localStorage:", payload.error.issues);
      return;
    }

    for (const key of OVERRIDABLE_ENV_VARS) {
      const value = overrideValueSchema.safeParse(payload.data[key]);
      if (value.success) {
        envOverrides[key] = value.data;
      }
    }
  } catch (error) {
    console.error("Error loading env overrides from localStorage:", error);
  }
}

/**
 * Get an environment variable value.
 * For overridable vars: returns the override if set and non-empty, otherwise the original value.
 * For all other vars: returns process.env value directly.
 */
export function getEnv(key: string): string | undefined {
  if (isOverridableEnvVar(key)) {
    const override = envOverrides[key];
    if (override && override.trim() !== "") {
      return override;
    }
    return originalEnvValues[key];
  }

  return getEnvValue(key);
}

const FALLBACK_PLUGIN_VERSION = "dev";

export function getPluginVersion(): string {
  return getEnv("PLUGIN_VERSION") || FALLBACK_PLUGIN_VERSION;
}

export function getAllEnv(): Record<OverridableEnvVar, string | undefined> {
  const result: Record<OverridableEnvVar, string | undefined> = {} as any;
  for (const key of OVERRIDABLE_ENV_VARS) {
    result[key] = getEnv(key);
  }
  return result;
}

export function getEnvOverrides(): Partial<Record<OverridableEnvVar, string>> {
  return { ...envOverrides };
}

export function hasEnvOverrides(): boolean {
  return Object.values(envOverrides).some(v => v && v.trim() !== "");
}

export function saveEnvOverrides(overrides: Partial<Record<OverridableEnvVar, string>>): boolean {
  const cleanedOverrides: Partial<Record<OverridableEnvVar, string>> = {};
  for (const key of OVERRIDABLE_ENV_VARS) {
    const value = overrides[key];
    if (value && value.trim() !== "") {
      cleanedOverrides[key] = value.trim();
    }
  }
  
  envOverrides = cleanedOverrides;
  
  try {
    if (Object.keys(cleanedOverrides).length > 0) {
      localStorage.setItem(ENV_STORAGE_KEY, JSON.stringify(cleanedOverrides));
    } else {
      localStorage.removeItem(ENV_STORAGE_KEY);
    }
    return true;
  } catch (error) {
    console.error("Error saving env overrides to localStorage:", error);
    return false;
  }
}

export function clearEnvOverrides(): void {
  envOverrides = {};
  try {
    localStorage.removeItem(ENV_STORAGE_KEY);
    localStorage.removeItem(DIOPTRA_LOGIN_METHOD);
  } catch (error) {
    console.error("Error clearing env overrides from localStorage:", error);
  }
}

export function getOriginalEnv(key: OverridableEnvVar): string | undefined {
  return originalEnvValues[key];
}

initEnvOverrides();

